/**
 * Génération du Rapport RSE Environnemental — PDF
 * Basé sur le modèle Tertiaire Solutions (Conformité Décret Tertiaire / DEET)
 * Données issues des capteurs WattelEM et équipements reliés
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { registerRobotoFont } from './fonts/registerRoboto';
import { translateIn, localeOf, preloadLanguage, getCurrentLanguage, type Language, type TranslationKey } from '../i18n';

// Langue du PDF : celle de l'interface, sauf l'hindi (jsPDF n'assemble pas l'écriture devanagari) → anglais.
let pdfLang: Language = 'fr';
const t = (key: TranslationKey, vars?: Record<string, string | number>) => translateIn(pdfLang, key, vars);
const currentLocale = () => localeOf(pdfLang);

// ─── Types ──────────────────────────────────────────────────────────

export interface RapportRSEInput {
  // Identité
  organisationName: string;
  periode: { startDate: string; endDate: string };

  // KPIs énergie (WattelEM)
  totalEnergyKwh: number;
  totalCost: number;
  totalDevices: number;    // Nb capteurs WattelEM
  activeDevices: number;   // Nb capteurs en ligne
  totalTransactions: number; // Non utilisé pour WattelEM (= 0)

  // Détails mensuels
  energyByMonth: Array<{ month: string; year: number; totalEnergy: number; transactionCount?: number }>;

  // Top devices (zones/installations les plus consommatrices)
  topDevices: Array<{ deviceName: string; totalEnergy: number; transactionCount?: number }>;

  // Revenus par device (optionnel, vide pour WattelEM)
  revenueByDevice: Array<{ deviceName: string; totalRevenue: number }>;

  // Énergie par véhicule (optionnel, vide pour WattelEM)
  energyByVehicle: Array<{ vehicleName: string; totalEnergy: number }>;

  // Architecture
  totalInstallations: number;
  totalZones: number;
  totalEquipment: number;
}

// ─── Constantes ─────────────────────────────────────────────────────

const FACTEUR_EMISSION_FR = 0.0569; // kgCO₂/kWh (mix électrique France 2023)

const GREEN_DARK: [number, number, number] = [10, 22, 40]; // ZCE Navy
const GREEN: [number, number, number] = [26, 107, 61];
const ORANGE: [number, number, number] = [230, 126, 34];
const BLUE: [number, number, number] = [30, 60, 114];
const WHITE: [number, number, number] = [255, 255, 255];
const GRAY_LIGHT: [number, number, number] = [245, 245, 245];

// ─── Helpers ────────────────────────────────────────────────────────

function formatNumber(n: number, decimals = 1): string {
  return n.toLocaleString(currentLocale(), { maximumFractionDigits: decimals });
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── Génération PDF ─────────────────────────────────────────────────

export async function generateRapportRSE(data: RapportRSEInput): Promise<jsPDF> {
  pdfLang = getCurrentLanguage() === 'hi' ? 'en' : getCurrentLanguage();
  await preloadLanguage(pdfLang);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Register Roboto font for full UTF-8 support (CO₂, accents, etc.)
  await registerRobotoFont(doc);
  doc.setFont('Roboto', 'normal');

  const pageWidth = 210;
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;

  // Calculs dérivés
  const emissionsCO2_t = (data.totalEnergyKwh * FACTEUR_EMISSION_FR) / 1000;
  const coutEstime = data.totalCost;
  const disponibilite = data.totalDevices > 0 ? ((data.activeDevices / data.totalDevices) * 100).toFixed(0) : '—';

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 1 — COUVERTURE
  // ═══════════════════════════════════════════════════════════════════

  // Bandeau supérieur
  doc.setFillColor(...GREEN_DARK);
  doc.rect(0, 0, pageWidth, 70, 'F');

  // Titre principal
  doc.setTextColor(...WHITE);
  doc.setFontSize(22);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_title'), pageWidth / 2, 28, { align: 'center' });

  doc.setFontSize(11);
  doc.setFont('Roboto', 'normal');
  doc.text(t('adm_pdf_subtitle'), pageWidth / 2, 38, { align: 'center' });

  doc.setFontSize(9);
  doc.text(`${data.organisationName}`, pageWidth / 2, 50, { align: 'center' });
  doc.text(t('adm_pdf_period', { start: formatDate(data.periode.startDate), end: formatDate(data.periode.endDate) }), pageWidth / 2, 58, { align: 'center' });

  // Tableau d'identité
  const identite = [
    [t('adm_pdf_id_organisation'), data.organisationName],
    [t('adm_pdf_id_period'), `${formatDate(data.periode.startDate)} — ${formatDate(data.periode.endDate)}`],
    [t('adm_pdf_id_installations'), String(data.totalInstallations)],
    [t('adm_pdf_id_zones'), String(data.totalZones)],
    [t('adm_pdf_id_equipment'), String(data.totalEquipment)],
    [t('adm_pdf_id_sensors'), String(data.totalDevices)],
    [t('adm_pdf_online_sensors'), `${data.activeDevices} / ${data.totalDevices} (${disponibilite} %)`],
    [t('adm_pdf_id_report_date'), new Date().toLocaleDateString(currentLocale())],
  ];

  let y = 85;
  // Section header
  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_general_info'), margin + 3, y + 5.5);
  y += 12;

  identite.forEach(([label, value], i) => {
    doc.setFillColor(i % 2 === 0 ? 245 : 255, i % 2 === 0 ? 245 : 255, i % 2 === 0 ? 245 : 255);
    doc.rect(margin, y, contentWidth, 8, 'F');
    doc.setTextColor(50, 50, 50);
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(8);
    doc.text(label, margin + 3, y + 5.5);
    doc.setFont('Roboto', 'normal');
    doc.text(value, margin + 80, y + 5.5);
    y += 8;
  });

  // Pied de page
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(t('adm_pdf_footer_generated'), pageWidth / 2, 285, { align: 'center' });
  doc.text('1 / 4', pageWidth / 2, 290, { align: 'center' });

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 2 — SYNTHÈSE & BILAN CARBONE
  // ═══════════════════════════════════════════════════════════════════

  doc.addPage();
  y = 15;

  // Section header
  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_h1'), margin + 3, y + 5.5);
  y += 14;

  // KPI Cards (4 blocs)
  const kpis = [
    { label: t('adm_pdf_kpi_energy'), value: `${formatNumber(data.totalEnergyKwh)} kWh`, color: GREEN },
    { label: t('adm_pdf_kpi_co2'), value: `${formatNumber(emissionsCO2_t, 2)} tCO₂e`, color: ORANGE },
    { label: t('adm_pdf_kpi_cost'), value: `${formatNumber(coutEstime, 2)} €`, color: BLUE },
    { label: t('adm_pdf_kpi_availability'), value: `${disponibilite} %`, color: GREEN_DARK },
  ];

  const cardWidth = (contentWidth - 6) / 4;
  kpis.forEach((kpi, i) => {
    const x = margin + i * (cardWidth + 2);
    doc.setFillColor(...GRAY_LIGHT);
    doc.rect(x, y, cardWidth, 24, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.rect(x, y, cardWidth, 24, 'S');

    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont('Roboto', 'normal');
    doc.text(kpi.label, x + 2, y + 6);

    doc.setFontSize(11);
    doc.setTextColor(...kpi.color);
    doc.setFont('Roboto', 'bold');
    doc.text(kpi.value, x + 2, y + 16);
  });
  y += 30;

  // Résumé textuel
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50);
  doc.setFont('Roboto', 'normal');
  doc.text(
    t('adm_pdf_summary_1', { sensors: data.totalDevices, kwh: formatNumber(data.totalEnergyKwh), zones: data.totalZones, equipment: data.totalEquipment }),
    margin, y, { maxWidth: contentWidth }
  );
  y += 10;
  doc.text(
    t('adm_pdf_summary_2', { t: formatNumber(emissionsCO2_t, 2), factor: FACTEUR_EMISSION_FR }),
    margin, y, { maxWidth: contentWidth }
  );
  y += 14;

  // ─── Section 2 : Bilan Carbone ───
  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_h2'), margin + 3, y + 5.5);
  y += 12;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[t('adm_rse_th_source'), 'kWh', t('adm_rse_th_factor'), 'tCO₂e']],
    body: [
      [t('adm_rse_elec_sensors'), formatNumber(data.totalEnergyKwh), String(FACTEUR_EMISSION_FR), formatNumber(emissionsCO2_t, 3)],
      [t('adm_rse_total'), formatNumber(data.totalEnergyKwh), '—', formatNumber(emissionsCO2_t, 3)],
    ],
    headStyles: { fillColor: GREEN, textColor: WHITE, fontSize: 8, fontStyle: 'bold', font: 'Roboto' },
    bodyStyles: { fontSize: 8, font: 'Roboto' },
    alternateRowStyles: { fillColor: GRAY_LIGHT },
    styles: { cellPadding: 2, font: 'Roboto' },
  });

  y = (doc as any).lastAutoTable.finalY + 10;

  // Indicateurs complémentaires
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[t('adm_pdf_th_indicator'), t('adm_pdf_th_value'), t('adm_pdf_th_unit')]],
    body: [
      [t('adm_pdf_avg_per_sensor'), formatNumber(data.totalDevices > 0 ? data.totalEnergyKwh / data.totalDevices : 0), 'kWh'],
      [t('adm_pdf_avg_cost'), formatNumber(coutEstime, 2), '€'],
      [t('adm_rse_kpi_carbon_intensity'), formatNumber(emissionsCO2_t * 1000 / (data.totalEnergyKwh || 1), 4), 'kgCO₂/kWh'],
    ],
    headStyles: { fillColor: BLUE, textColor: WHITE, fontSize: 8, fontStyle: 'bold', font: 'Roboto' },
    bodyStyles: { fontSize: 8, font: 'Roboto' },
    alternateRowStyles: { fillColor: GRAY_LIGHT },
    styles: { cellPadding: 2, font: 'Roboto' },
  });

  // Pied de page
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(t('adm_pdf_footer_factor'), pageWidth / 2, 285, { align: 'center' });
  doc.text('2 / 4', pageWidth / 2, 290, { align: 'center' });

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 3 — ÉVOLUTION MENSUELLE & TOP ZONES
  // ═══════════════════════════════════════════════════════════════════

  doc.addPage();
  y = 15;

  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_h3'), margin + 3, y + 5.5);
  y += 12;

  if (data.energyByMonth.length > 0) {
    const monthRows = data.energyByMonth.map((m, i) => {
      const evolPrev = i > 0
        ? (((m.totalEnergy - data.energyByMonth[i - 1].totalEnergy) / (data.energyByMonth[i - 1].totalEnergy || 1)) * 100).toFixed(1) + ' %'
        : '—';
      return [
        `${m.month}/${m.year}`,
        formatNumber(m.totalEnergy),
        evolPrev,
      ];
    });

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [[t('adm_rse_th_month'), 'kWh', t('adm_rse_th_mom')]],
      body: monthRows,
      headStyles: { fillColor: GREEN, textColor: WHITE, fontSize: 8, fontStyle: 'bold', font: 'Roboto' },
      bodyStyles: { fontSize: 7.5, font: 'Roboto' },
      alternateRowStyles: { fillColor: GRAY_LIGHT },
      styles: { cellPadding: 2, font: 'Roboto' },
    });

    y = (doc as any).lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(t('adm_pdf_no_monthly'), margin, y);
    y += 10;
  }

  // Top zones / capteurs
  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_h4'), margin + 3, y + 5.5);
  y += 12;

  if (data.topDevices.length > 0) {
    const totalEnergy = data.topDevices.reduce((s, d) => s + d.totalEnergy, 0);
    const deviceRows = data.topDevices.map(d => [
      d.deviceName,
      formatNumber(d.totalEnergy),
      ((d.totalEnergy / (totalEnergy || 1)) * 100).toFixed(1) + ' %',
    ]);

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [[t('adm_pdf_th_zone_installation'), 'kWh', t('adm_rse_th_share')]],
      body: deviceRows,
      headStyles: { fillColor: BLUE, textColor: WHITE, fontSize: 8, fontStyle: 'bold', font: 'Roboto' },
      bodyStyles: { fontSize: 7.5, font: 'Roboto' },
      alternateRowStyles: { fillColor: GRAY_LIGHT },
      styles: { cellPadding: 2, font: 'Roboto' },
    });
  }

  // Pied de page
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('3 / 4', pageWidth / 2, 290, { align: 'center' });

  // ═══════════════════════════════════════════════════════════════════
  // PAGE 4 — INFRASTRUCTURE & CONCLUSION
  // ═══════════════════════════════════════════════════════════════════

  doc.addPage();
  y = 15;

  // Infrastructure
  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_h5'), margin + 3, y + 5.5);
  y += 12;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [[t('adm_pdf_th_indicator'), t('adm_pdf_th_value')]],
    body: [
      [t('adm_pdf_infra_installations'), String(data.totalInstallations)],
      [t('adm_pdf_infra_zones'), String(data.totalZones)],
      [t('adm_pdf_infra_equipment'), String(data.totalEquipment)],
      [t('adm_pdf_infra_sensors'), String(data.totalDevices)],
      [t('adm_pdf_online_sensors'), String(data.activeDevices)],
      [t('adm_pdf_infra_availability'), `${disponibilite} %`],
    ],
    headStyles: { fillColor: BLUE, textColor: WHITE, fontSize: 8, fontStyle: 'bold', font: 'Roboto' },
    bodyStyles: { fontSize: 8, font: 'Roboto' },
    alternateRowStyles: { fillColor: GRAY_LIGHT },
    styles: { cellPadding: 2, font: 'Roboto' },
  });

  y = (doc as any).lastAutoTable.finalY + 15;

  // ─── CONCLUSION ───
  doc.setFillColor(...GREEN);
  doc.rect(margin, y, contentWidth, 8, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont('Roboto', 'bold');
  doc.text(t('adm_pdf_h6'), margin + 3, y + 5.5);
  y += 14;

  // Bandeau de conclusion
  const conclusionColor: [number, number, number] = emissionsCO2_t < 1 ? GREEN : emissionsCO2_t < 10 ? ORANGE : [192, 57, 43];
  const conclusionText = emissionsCO2_t < 1
    ? t('adm_pdf_impact_low')
    : emissionsCO2_t < 10
      ? t('adm_pdf_impact_moderate')
      : t('adm_pdf_impact_high');

  doc.setFillColor(conclusionColor[0], conclusionColor[1], conclusionColor[2], 20);
  doc.setDrawColor(...conclusionColor);
  doc.roundedRect(margin, y, contentWidth, 22, 3, 3, 'FD');
  doc.setTextColor(...conclusionColor);
  doc.setFontSize(10);
  doc.setFont('Roboto', 'bold');
  doc.text(conclusionText, pageWidth / 2, y + 9, { align: 'center' });
  doc.setFontSize(8);
  doc.setFont('Roboto', 'normal');
  doc.text(
    t('adm_pdf_banner_line', { kwh: formatNumber(data.totalEnergyKwh), t: formatNumber(emissionsCO2_t, 2), sensors: data.totalDevices, equipment: data.totalEquipment }),
    pageWidth / 2,
    y + 17,
    { align: 'center' }
  );
  y += 30;

  // Texte conclusion
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50);
  doc.setFont('Roboto', 'normal');
  const conclusionParagraph = [
    t('adm_pdf_concl_1'),
    t('adm_pdf_concl_2', { org: data.organisationName, start: formatDate(data.periode.startDate), end: formatDate(data.periode.endDate) }),
    ``,
    t('adm_pdf_concl_key_points'),
    `• ${t('adm_pdf_concl_sensors', { sensors: data.totalDevices, online: data.activeDevices, pct: disponibilite })}`,
    `• ${t('adm_pdf_concl_kwh', { kwh: formatNumber(data.totalEnergyKwh) })}`,
    `• ${t('adm_pdf_concl_emissions', { t: formatNumber(emissionsCO2_t, 2) })}`,
    `• ${t('adm_pdf_concl_infra', { installations: data.totalInstallations, zones: data.totalZones, equipment: data.totalEquipment })}`,
    `• ${t('adm_pdf_concl_cost', { cost: formatNumber(coutEstime, 2) })}`,
    ``,
    t('adm_pdf_concl_3'),
    t('adm_pdf_concl_4'),
  ];

  conclusionParagraph.forEach(line => {
    doc.text(line, margin, y);
    y += 4.5;
  });

  // Pied de page final
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(t('adm_pdf_footer_final', { year: new Date().getFullYear() }), pageWidth / 2, 285, { align: 'center' });
  doc.text('4 / 4', pageWidth / 2, 290, { align: 'center' });

  return doc;
}
